<template>
  <v-dialog
    :value="true"
    max-width="1024px"
    scrollable
    persistent
    @input="close"
  >
    <v-card :loading="formProcessing">
      <v-card-title>
        <span class="headline">{{ formTitle }}</span>
      </v-card-title>

      <v-card-text>
        <v-container>
          <v-form>
            <v-row>
              <!-- Campaign selection for testing -->
              <v-col cols="6">
                <v-select
                  v-model="formData.campaign_id"
                  :items="campaignOptions"
                  required
                  label="Campaign"
                  :error-messages="campaignErrors"
                  @input="$v.formData.campaign_id.$touch()"
                  @blur="$v.formData.campaign_id.$touch()"
                />
              </v-col>

              <!-- Enable/Disable campaign -->
              <v-col cols="6">
                <v-select
                  v-model="formData.active"
                  :items="activeOptions"
                  required
                  label="Enabled"
                  :error-messages="activeErrors"
                  @input="$v.formData.active.$touch()"
                  @blur="$v.formData.active.$touch()"
                />
              </v-col>
            </v-row>

            <!-- Redirect campaigns for SSNs ending in 0-9 -->
            <v-row>
              <v-col
                v-for="i in 10"
                :key="i"
                cols="6"
              >
                <v-select
                  v-model="formData[`ssn_ends_in_${i - 1}`]"
                  :items="redirectCampaignOptions"
                  required
                  :label="`SSN Ends in ${i - 1}`"
                  :error-messages="ssnErrors(i - 1)"
                  @input="$v.formData[`ssn_ends_in_${i - 1}`].$touch()"
                  @blur="$v.formData[`ssn_ends_in_${i - 1}`].$touch()"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />

        <v-btn
          color="blue darken-1"
          text
          :disabled="formProcessing"
          @click="close()"
        >
          Cancel
        </v-btn>

        <v-btn
          color="blue darken-1"
          text
          :disabled="formProcessing"
          @click="submitForm()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import { mapActions } from 'vuex';

export default {
  props: {
    initialData: {
      type: Object,
      default: () => {},
    },

    campaigns: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      formProcessing: false,

      activeOptions: [
        { value: true, text: 'true' },
        { value: false, text: 'false' },
      ],

      formData: {
        campaign_id: '',
        ssn_ends_in_0: null,
        ssn_ends_in_1: null,
        ssn_ends_in_2: null,
        ssn_ends_in_3: null,
        ssn_ends_in_4: null,
        ssn_ends_in_5: null,
        ssn_ends_in_6: null,
        ssn_ends_in_7: null,
        ssn_ends_in_8: null,
        ssn_ends_in_9: null,
        active: false,
      },
    };
  },

  validations() {
    return {
      formData: {
        campaign_id: { required },
        ssn_ends_in_0: { required },
        ssn_ends_in_1: { required },
        ssn_ends_in_2: { required },
        ssn_ends_in_3: { required },
        ssn_ends_in_4: { required },
        ssn_ends_in_5: { required },
        ssn_ends_in_6: { required },
        ssn_ends_in_7: { required },
        ssn_ends_in_8: { required },
        ssn_ends_in_9: { required },
        active: { required },
      },
    };
  },

  computed: {
    formTitle() {
      return this.isNewItem ? 'Add Campaign Testing' : 'Edit Campaign Testing';
    },

    isNewItem() {
      return !this.initialData.id;
    },

    campaignOptions() {
      return this.campaigns.map((item) => ({
        value: item.id,
        text: item.campaign_name,
      }));
    },

    redirectCampaignOptions() {
      return this.campaigns
        .map((item) => ({
          value: item.id,
          text: item.campaign_name,
        }));
    },

    campaignErrors() {
      const errors = [];
      if (this.$v.formData.campaign_id.$dirty && !this.$v.formData.campaign_id.required) {
        errors.push('Campaign is required');
      }
      return errors;
    },

    activeErrors() {
      const errors = [];
      if (this.$v.formData.active.$dirty && !this.$v.formData.active.required) {
        errors.push('Enabled is required');
      }
      return errors;
    },
  },

  created() {
    this.formData = Object.assign(this.formData, this.initialData);
  },

  methods: {
    ...mapActions('notification', ['setNotification']),

    submitForm() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        return;
      }

      this.formProcessing = true;

      const payload = { ...this.formData };

      // Determine if we are creating a new campaign or updating an existing one
      const request = this.isNewItem
        ? this.$axios.post('api/v1/test-campaigns', payload)
        : this.$axios.put(`api/v1/test-campaigns/${this.initialData.id}`, payload);

      request
        .then(() => {
          this.setNotification({
            message: 'Campaign Testing saved successfully',
            type: 'success',
          });

          this.$emit('item-saved');
          this.close();
        })
        .catch((error) => {
          if (error.response.data.message) {
            this.setNotification({
              message: error.response.data.message,
              color: 'red',
            });
          } else {
            this.setNotification({
              message: 'There was an error processing your request.',
              color: 'red',
            });
          }
        })
        .finally(() => {
          this.formProcessing = false;
        });
    },

    close() {
      this.$v.$reset();
      this.$emit('close-modal');
    },

    // Generate error messages for SSN fields
    ssnErrors(index) {
      const errors = [];
      const ssnField = this.$v.formData[`ssn_ends_in_${index}`];
      if (ssnField.$dirty && !ssnField.required) {
        errors.push(`Campaign for SSN Ends in ${index} is required`);
      }
      return errors;
    },
  },
};
</script>

<style></style>
